import React, { useState, useEffect, useRef, forwardRef, useMemo } from 'react';
import axios from 'axios';
import { Table, Button, Offcanvas, Form, Row, Col, Modal, Dropdown } from 'react-bootstrap';
import Swal from 'sweetalert2';
import LicenseInfoDetails from './LicenseInfoDetails';
import TenantInfoDetails from '../v360-setup-info/TenantInfoDetails';
import ReactPaginate from 'react-paginate';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  }
});

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const userInfo = JSON.parse(localStorage.getItem('user-info') || '{}');
  const token = userInfo?.token;
  const origin = localStorage.getItem('user-origin');
  return {
    Authorization: `Bearer ${token}`,
    'Tenant-Origin': origin
  };
};


const CustomToggle = forwardRef(({ children, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className="btn rounded border d-flex align-items-center justify-content-center shadow-sm bg-white text-muted px-3"
    style={{ height: '38px' }}
    title="Filter by Expiry"
  >
    <i className="mdi mdi-tune font-size-18 me-2"></i> Filter
  </button>
));

const LicenseInfoList = ({ setupId, isEmbedded, closeParentOffcanvas, onChildModalToggle, activeExpiryFilter, autoOpenLicenseId }) => {
  const [licenseInfos, setLicenseInfos] = useState([]);
  const [setupInfos, setSetupInfos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [modalStartDate, setModalStartDate] = useState(null);
  const [modalValidityDate, setModalValidityDate] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [userConcurrency, setUserConcurrency] = useState('1:1');
  const [licenseStatus, setLicenseStatus] = useState('active');
  const [errors, setErrors] = useState({});
  const [rolesState, setRolesState] = useState({
    role_s_admin: 0,
    role_admin: 0,
    role_supervisor: 0,
    role_teamlead: 0,
    role_analyst: 0,
    role_cagent: 0,
    role_vagent: 0,
    role_tagent: 0,
    role_agent: 0,
    role_agent_plus: 0,
  });

  const calculatedUserLogin = useMemo(() => {
    return Object.values(rolesState).reduce((acc, val) => acc + (Number(val) || 0), 0);
  }, [rolesState]);

  const calculatedUserCreate = useMemo(() => {
    let multiplier = 1;
    if (userConcurrency === '2:1') multiplier = 2;
    else if (userConcurrency === '3:1') multiplier = 3;
    return calculatedUserLogin * multiplier;
  }, [calculatedUserLogin, userConcurrency]);

  const handleRoleChange = (roleName, value) => {
    const num = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
    setRolesState(prev => ({
      ...prev,
      [roleName]: num
    }));
  };
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [expiryFilter, setExpiryFilter] = useState(activeExpiryFilter || '');
  const [sortConfig, setSortConfig] = useState({ key: 'tenant_name', direction: 'asc' });
  const [accStatusTab, setAccStatusTab] = useState('all'); // 'all' | 'active' | 'deactivated' | 'suspended'
  const [selectedSetupFilter, setSelectedSetupFilter] = useState(null);
  const [selectedTenantFilter, setSelectedTenantFilter] = useState(null);
  const [setupSearchInput, setSetupSearchInput] = useState('');
  const [tenantSearchInput, setTenantSearchInput] = useState('');

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };
  
  useEffect(() => {
    if (activeExpiryFilter !== undefined) {
      setExpiryFilter(activeExpiryFilter);
    }
  }, [activeExpiryFilter]);

  const handleExpiryFilterChange = (e) => {
    setExpiryFilter(e.target.value);
    setCurrentPage(0);
  };
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendData, setExtendData] = useState({ type: 'days', value: 1 });
  const [selectedTenantData, setSelectedTenantData] = useState(null);
  const [showTenantDetails, setShowTenantDetails] = useState(false);

  const handleOpenTenantDetails = (tenant) => {
    setSelectedTenantData(tenant);
    setShowTenantDetails(true);
  };
  
  useEffect(() => {
    fetchLicenseInfos(expiryFilter);
  }, [setupId, activeExpiryFilter, expiryFilter]);

  useEffect(() => {
    fetchSetupInfos();
  }, []);

  const handleExtendSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${backendUrl}/api/v360-license-info/${currentRecord.id}`, {
        extension_type: extendData.type,
        extension_days: parseInt(extendData.value, 10)
      }, { headers: getHeaders() });
      Toast.fire({ icon: 'success', title: 'License extended successfully' });
      setShowExtendModal(false);
      fetchLicenseInfos();
    } catch (error) {
      console.error('Error extending license:', error);
      Toast.fire({ icon: 'error', title: 'Failed to extend license' });
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0);
  };

  const fetchLicenseInfos = async (overrideFilter) => {
    try {
      const filter = typeof overrideFilter === 'string' ? overrideFilter : expiryFilter;
      const params = {};
      if (setupId) {
        params.setup_id = setupId;
      }
      if (filter) {
        params.expiry_filter = filter;
      }
      const response = await axios.get(`${backendUrl}/api/v360-license-info`, { params, headers: getHeaders() });
      const resData = response.data;
      if (resData && Array.isArray(resData.data)) {
        setLicenseInfos(resData.data);
      } else if (Array.isArray(resData)) {
        setLicenseInfos(resData);
      } else {
        setLicenseInfos([]);
      }
    } catch (error) {
      console.error('Error fetching license infos', error);
      Toast.fire({ icon: 'error', title: 'Failed to load license infos' });
    }
  };

  useEffect(() => {
    if (onChildModalToggle) {
      onChildModalToggle(showExtendModal || showDetailsModal || showTenantDetails);
    }
  }, [showExtendModal, showDetailsModal, showTenantDetails, onChildModalToggle]);

  const fetchSetupInfos = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/v360-setup-info`, { headers: getHeaders() });
      setSetupInfos(response.data);
    } catch (error) {
      console.error('Error fetching setup infos', error);
    }
  };

  const handleShowModal = (record = null) => {
    setCurrentRecord(record);
    if (record) {
      setModalStartDate(record.start_date ? new Date(record.start_date) : null);
      const activeValidity = record.extended_validity || record.validity;
      setModalValidityDate(activeValidity ? new Date(activeValidity) : null);
      setUserConcurrency(record.user_concurrency || '1:1');
      setLicenseStatus(record.license_status || 'active');
      setRolesState({
        role_s_admin: Number(record.role_s_admin) || 0,
        role_admin: Number(record.role_admin) || 0,
        role_supervisor: Number(record.role_supervisor) || 0,
        role_teamlead: Number(record.role_teamlead) || 0,
        role_analyst: Number(record.role_analyst) || 0,
        role_cagent: Number(record.role_cagent) || 0,
        role_vagent: Number(record.role_vagent) || 0,
        role_tagent: Number(record.role_tagent) || 0,
        role_agent: Number(record.role_agent) || 0,
        role_agent_plus: Number(record.role_agent_plus) || 0,
      });
    } else {
      setModalStartDate(null);
      setModalValidityDate(null);
      setUserConcurrency('1:1');
      setLicenseStatus('active');
      setRolesState({
        role_s_admin: 0,
        role_admin: 0,
        role_supervisor: 0,
        role_teamlead: 0,
        role_analyst: 0,
        role_cagent: 0,
        role_vagent: 0,
        role_tagent: 0,
        role_agent: 0,
        role_agent_plus: 0,
      });
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setCurrentRecord(null);
    setModalStartDate(null);
    setModalValidityDate(null);
    setUserConcurrency('1:1');
    setLicenseStatus('active');
    setRolesState({
      role_s_admin: 0,
      role_admin: 0,
      role_supervisor: 0,
      role_teamlead: 0,
      role_analyst: 0,
      role_cagent: 0,
      role_vagent: 0,
      role_tagent: 0,
      role_agent: 0,
      role_agent_plus: 0,
    });
    setErrors({});
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    if (!isEmbedded && !data.setup_id) {
      newErrors.setup_id = 'Setup is required';
    }

    const formatDateToIso = (dateStr) => {
      if (!dateStr) return null;
      const str = String(dateStr).trim();
      const parts = str.split('-');
      if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return str;
    };

    const formatDateObjToIso = (dateObj) => {
      if (!dateObj) return null;
      const d = new Date(dateObj);
      if (isNaN(d.getTime())) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const formattedStartDate = data.start_date ? formatDateToIso(data.start_date) : formatDateObjToIso(modalStartDate);
    const formattedValidityDate = data.validity ? formatDateToIso(data.validity) : formatDateObjToIso(modalValidityDate);

    if (!formattedStartDate) {
      newErrors.start_date = 'Start Date is required';
    }

    if (!formattedValidityDate) {
      newErrors.validity = 'Validity (End Date) is required';
    }

    if (formattedStartDate && formattedValidityDate) {
      const startDateObj = new Date(formattedStartDate);
      const validityDateObj = new Date(formattedValidityDate);
      startDateObj.setHours(0, 0, 0, 0);
      validityDateObj.setHours(0, 0, 0, 0);

      if (validityDateObj < startDateObj) {
        newErrors.validity = 'Validity (End Date) cannot be less than Start Date';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = newErrors.start_date || newErrors.validity || newErrors.setup_id || 'Please fill in all required fields';
      Toast.fire({ icon: 'error', title: firstError });
      return;
    }

    setErrors({});

    const numericFields = [
      'setup_id', 'tenant_id', 'user_login', 'user_create', 
      'role_s_admin', 'role_admin', 'role_supervisor', 'role_teamlead', 'role_analyst', 
      'role_cagent', 'role_vagent', 'role_tagent', 'role_agent', 'mobile_app', 
      'module_email', 'module_voicemail', 'module_chat', 'module_interaction', 
      'module_voice', 'module_api', 'role_agent_plus', 'module_video', 'wallboard', 
      'gen_ai', 'toolbar_app', 'toolbar_app_sup', 'ai_assist', 'ai_assist_pro',
      'module_ticket', 'camp_voice', 'camp_waba', 'camp_sms', 'camp_email'
    ];
    
    for (let field of numericFields) {
      if (data[field] !== undefined) {
        if (data[field] === "") {
          data[field] = 0;
        } else {
          const parsed = Number(data[field]);
          if (isNaN(parsed)) {
            Toast.fire({ icon: 'error', title: `${field.replace('_', ' ').toUpperCase()} must be a valid number` });
            return;
          }
          data[field] = parsed;
        }
      }
    }

    data.user_login = calculatedUserLogin;
    data.user_create = calculatedUserCreate;
    data.user_concurrency = userConcurrency;
    data.license_status = licenseStatus;
    data.toolbar_app_sup = Number(data.toolbar_app_sup) || 0;

    data.start_date = formattedStartDate;
    data.validity = formattedValidityDate;

    if (formData.has('tenant_name')) {
      data.tenant_name = data.tenant_name ? String(data.tenant_name).trim() : null;
    } else if (currentRecord?.tenant_name) {
      data.tenant_name = currentRecord.tenant_name;
    }
    data.acc_status = data.acc_status ? String(data.acc_status).toLowerCase().trim() : 'active';
    data.status_reason = data.status_reason ? String(data.status_reason).trim() : '';

    data.ip_check = formData.get('ip_check') === 'on';
    data.ips = data.ips ? String(data.ips).split(',').map(ip => ip.trim()).filter(Boolean) : [];

    try {
      if (currentRecord && currentRecord.id) {
        await axios.patch(`${backendUrl}/api/v360-license-info/${currentRecord.id}`, data, { headers: getHeaders() });
        Toast.fire({ icon: 'success', title: 'Record updated successfully' });
      } else {
        await axios.post(`${backendUrl}/api/v360-license-info`, data, { headers: getHeaders() });
        Toast.fire({ icon: 'success', title: 'Record created successfully' });
      }
      handleCloseModal();
      fetchLicenseInfos();
    } catch (error) {
      console.error('Error saving record', error);
      Toast.fire({ icon: 'error', title: 'Failed to save record' });
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${backendUrl}/api/v360-license-info/${id}`, { headers: getHeaders() });
          Swal.fire('Deleted!', 'Record has been deleted.', 'success');
          fetchLicenseInfos();
        } catch (error) {
          console.error('Error deleting record', error);
          Swal.fire('Error!', 'Failed to delete record.', 'error');
        }
      }
    });
  };

  const parseValidDate = (dateStr) => {
      if (!dateStr) return null;
      const datePart = String(dateStr).split('T')[0];
      const parts = datePart.split('-');
      if (parts.length === 3) {
          if (parts[0].length === 4) {
              return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
          } else {
              return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
          }
      }
      const d = new Date(dateStr);
      return isNaN(d) ? null : d;
  };

  const getRowColorClass = (info) => {
    const validityDateStr = info.extended_validity || info.validity;
    if (!validityDateStr) return '';
    const validityDate = parseValidDate(validityDateStr);
    if (!validityDate) return '';
    validityDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const diffTime = validityDate - today;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return 'text-danger fw-bold'; // Expired
    } else if (diffDays <= 30) {
      return 'text-warning fw-bold'; // Expiring in <= 30 days
    }
    return '';
  };

  const getLicenseStatus = (info) => {
      const validityDateStr = info?.extended_validity || info?.validity;
      if (!validityDateStr) return 'Active';
      const validityDate = parseValidDate(validityDateStr);
      if (!validityDate) return 'Active';
      validityDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const diffTime = validityDate - today;
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays >= -7 && diffDays < 0) return 'Expired in 7 days';
      if (diffDays < 0) return 'Expired';
      if (info?.extended_validity) return 'Extended';
      return 'Active';
  };

  const getSetupName = (info) => {
    if (info.setup_name) return info.setup_name;
    if (info.setup?.client_name) return info.setup.client_name;
    if (info.setup?.setup_name) return info.setup.setup_name;
    if (info.setup_info?.client_name) return info.setup_info.client_name;
    if (info.setup_info?.setup_name) return info.setup_info.setup_name;
    if (info.client_name) return info.client_name;
    if (info.setup_id && setupInfos && setupInfos.length > 0) {
      const found = setupInfos.find(s => String(s.id) === String(info.setup_id));
      if (found) return found.client_name || found.setup_name || found.key || `Setup ${info.setup_id}`;
    }
    return info.setup_id ? `Setup ${info.setup_id}` : '-';
  };

  const getItemAccStatus = (info) => {
    const st = String(info.acc_status || 'active').trim().toLowerCase();
    if (st === 'deactivated' || st === 'inactive') return 'deactivated';
    if (st === 'suspended') return 'suspended';
    return 'active';
  };

  // Group licenses by (setup_id + tenant_name + tenant_id) so each unique tenant under each setup is its own row
  const allGroupedTenants = useMemo(() => {
    let list = licenseInfos;
    if (setupId) {
      list = list.filter(info => info.setup_id === setupId);
    }
    if (autoOpenLicenseId) {
      list = list.filter(info => info.id === autoOpenLicenseId || info.license_id === autoOpenLicenseId);
    }

    const map = new Map();

    list.forEach((lic) => {
      const rawName = lic.tenant_name;
      const isNull = rawName === null || rawName === undefined || String(rawName).trim() === '' || String(rawName).trim().toLowerCase() === 'null';
      
      const normName = isNull ? '__null__' : String(rawName).trim().toLowerCase();
      const normSetup = lic.setup_id !== null && lic.setup_id !== undefined ? String(lic.setup_id).trim() : '__no_setup__';
      const normTenantId = (lic.tenant_id !== null && lic.tenant_id !== undefined && String(lic.tenant_id).trim() !== '')
        ? String(lic.tenant_id).trim()
        : '__no_tenant_id__';
      
      const groupKey = `${normSetup}___${normName}___${normTenantId}`;
      const displayName = isNull ? null : String(rawName).trim();

      if (!map.has(groupKey)) {
        map.set(groupKey, {
          groupKey,
          tenant_name: displayName,
          isNull,
          setup_id: lic.setup_id || null,
          tenant_id: (lic.tenant_id !== null && lic.tenant_id !== undefined && String(lic.tenant_id).trim() !== '') ? lic.tenant_id : null,
          licenses: []
        });
      }

      const sName = getSetupName(lic);
      map.get(groupKey).licenses.push({
        ...lic,
        setup_name: sName
      });
    });

    const groups = [];
    map.forEach((g) => {
      const tenantIdSet = new Set();
      const remarkSet = new Set();

      let minStartDate = null;
      let maxEndDate = null;

      let hasActive = false;
      let hasSuspended = false;
      let allDeactivated = true;

      const sName = g.licenses[0]?.setup_name || (g.licenses[0] ? getSetupName(g.licenses[0]) : '-');

      let latestLicense = g.licenses[0] || null;

      g.licenses.forEach((lic) => {
        if (lic.tenant_id !== null && lic.tenant_id !== undefined && String(lic.tenant_id).trim() !== '') {
          tenantIdSet.add(String(lic.tenant_id).trim());
        }
        if (lic.remark && String(lic.remark).trim()) {
          remarkSet.add(String(lic.remark).trim());
        }

        if (lic.start_date) {
          const sDate = parseValidDate(lic.start_date);
          if (sDate) {
            if (!minStartDate || sDate < minStartDate) {
              minStartDate = sDate;
            }
          }
        }

        const endStr = lic.extended_validity || lic.validity;
        if (endStr) {
          const eDate = parseValidDate(endStr);
          if (eDate) {
            if (!maxEndDate || eDate > maxEndDate) {
              maxEndDate = eDate;
              latestLicense = lic;
            }
          }
        }

        const st = getItemAccStatus(lic);
        if (st === 'active') hasActive = true;
        if (st === 'suspended') hasSuspended = true;
        if (st !== 'deactivated') allDeactivated = false;
      });

      let acc_status = 'active';
      if (allDeactivated && g.licenses.length > 0) {
        acc_status = 'deactivated';
      } else if (hasSuspended && !hasActive) {
        acc_status = 'suspended';
      } else if (hasActive) {
        acc_status = 'active';
      } else if (g.licenses[0]?.acc_status) {
        acc_status = getItemAccStatus(g.licenses[0]);
      }

      // Compute license status strictly based on the latest end date license
      let licStatus = 'Active';
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (maxEndDate) {
        const diffTime = maxEndDate - today;
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays >= -7 && diffDays < 0) {
          licStatus = 'Expired in 7 days';
        } else if (diffDays < 0) {
          licStatus = 'Expired';
        } else if (latestLicense?.extended_validity) {
          licStatus = 'Extended';
        } else {
          licStatus = 'Active';
        }
      } else if (latestLicense?.extended_validity) {
        licStatus = 'Extended';
      } else {
        licStatus = 'Active';
      }

      const tenantIds = Array.from(tenantIdSet);
      const remarks = Array.from(remarkSet);
      const resolvedTenantId = g.tenant_id !== undefined ? g.tenant_id : (tenantIds.length > 0 ? tenantIds[0] : null);

      groups.push({
        groupKey: g.groupKey,
        tenant_name: g.tenant_name,
        isNull: g.isNull,
        setup_id: g.setup_id,
        setup_name: sName || '-',
        tenant_id: resolvedTenantId,
        tenant_ids: tenantIds,
        first_tenant_id_num: resolvedTenantId !== null && !isNaN(Number(resolvedTenantId)) ? Number(resolvedTenantId) : null,
        minStartDate,
        maxEndDate,
        startDateFormatted: minStartDate ? dayjs(minStartDate).format('DD-MM-YYYY') : '-',
        endDateFormatted: maxEndDate ? dayjs(maxEndDate).format('DD-MM-YYYY') : '-',
        remark: g.licenses[0]?.remark || '-',
        acc_status,
        license_status: licStatus,
        licenses: g.licenses,
        totalLicenses: g.licenses.length,
        primarySetupId: g.setup_id,
        firstLicense: g.licenses[0]
      });
    });

    return groups;
  }, [licenseInfos, setupId, autoOpenLicenseId, setupInfos]);

  const statusCounts = useMemo(() => {
    let active = 0;
    let deactivated = 0;
    let suspended = 0;

    if (!isEmbedded) {
      let list = allGroupedTenants;
      if (selectedSetupFilter) {
        list = list.filter(t => String(t.setup_id) === String(selectedSetupFilter.value));
      }
      if (selectedTenantFilter) {
        list = list.filter(t => 
          t.tenant_name && 
          t.tenant_name.toLowerCase().trim() === selectedTenantFilter.value.toLowerCase().trim()
        );
      }
      list.forEach((t) => {
        const st = t.acc_status;
        if (st === 'deactivated') deactivated++;
        else if (st === 'suspended') suspended++;
        else active++;
      });

      return {
        all: list.length,
        active,
        deactivated,
        suspended
      };
    }

    let baseList = licenseInfos;
    if (setupId) {
      baseList = baseList.filter(info => info.setup_id === setupId);
    }
    if (autoOpenLicenseId) {
      baseList = baseList.filter(info => info.id === autoOpenLicenseId || info.license_id === autoOpenLicenseId);
    }

    baseList.forEach((info) => {
      const st = getItemAccStatus(info);
      if (st === 'deactivated') deactivated++;
      else if (st === 'suspended') suspended++;
      else active++;
    });

    return {
      all: baseList.length,
      active,
      deactivated,
      suspended
    };
  }, [licenseInfos, setupId, autoOpenLicenseId, isEmbedded, allGroupedTenants, selectedSetupFilter, selectedTenantFilter]);

  // Available setup options for filter
  const setupFilterOptions = useMemo(() => {
    const setupMap = new Map();
    if (Array.isArray(setupInfos)) {
      setupInfos.forEach(s => {
        const label = s.client_name || s.setup_name || s.key || `Setup ${s.id}`;
        setupMap.set(String(s.id), { value: String(s.id), label });
      });
    }
    if (Array.isArray(licenseInfos)) {
      licenseInfos.forEach(lic => {
        if (lic.setup_id && !setupMap.has(String(lic.setup_id))) {
          const label = getSetupName(lic);
          setupMap.set(String(lic.setup_id), { value: String(lic.setup_id), label });
        }
      });
    }
    return Array.from(setupMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [setupInfos, licenseInfos]);

  // Available tenant options for filter (filtered by selected setup if any)
  const tenantFilterOptions = useMemo(() => {
    let sourceList = licenseInfos;
    if (selectedSetupFilter) {
      sourceList = sourceList.filter(l => String(l.setup_id) === String(selectedSetupFilter.value));
    }
    const tenantSet = new Set();
    const options = [];
    if (Array.isArray(sourceList)) {
      sourceList.forEach(lic => {
        const name = lic.tenant_name ? String(lic.tenant_name).trim() : null;
        if (name && !tenantSet.has(name.toLowerCase())) {
          tenantSet.add(name.toLowerCase());
          options.push({ value: name, label: name });
        }
      });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [licenseInfos, selectedSetupFilter]);

  const filteredSetupOptions = useMemo(() => {
    if (!setupSearchInput || !setupSearchInput.trim()) return setupFilterOptions;
    const q = setupSearchInput.toLowerCase().trim();
    return setupFilterOptions.filter(opt => opt.label.toLowerCase().includes(q));
  }, [setupFilterOptions, setupSearchInput]);

  const filteredTenantOptions = useMemo(() => {
    if (!tenantSearchInput || !tenantSearchInput.trim()) return tenantFilterOptions;
    const q = tenantSearchInput.toLowerCase().trim();
    return tenantFilterOptions.filter(opt => opt.label.toLowerCase().includes(q));
  }, [tenantFilterOptions, tenantSearchInput]);

  // Filtered grouped tenants for standalone view
  const filteredTenants = useMemo(() => {
    let list = allGroupedTenants;

    // Filter by Setup
    if (selectedSetupFilter) {
      list = list.filter(t => String(t.setup_id) === String(selectedSetupFilter.value));
    }

    // Filter by Tenant Name
    if (selectedTenantFilter) {
      list = list.filter(t => 
        t.tenant_name && 
        t.tenant_name.toLowerCase().trim() === selectedTenantFilter.value.toLowerCase().trim()
      );
    }

    // Filter by Account Status tab
    if (accStatusTab !== 'all') {
      list = list.filter(t => t.acc_status === accStatusTab);
    }

    // Client-side search matching tenant name, tenant id, setup name, acc status, license status, remark
    if (searchQuery && searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((t) => {
        const tenantName = (t.tenant_name || (t.isNull ? 'null' : '')).toLowerCase();
        const tenantId = String(t.tenant_id ?? '').toLowerCase();
        const setupName = (t.setup_name || '').toLowerCase();
        const accSt = (t.acc_status || '').toLowerCase();
        const licSt = (t.license_status || '').toLowerCase();
        const remark = (t.remark || '').toLowerCase();

        const licRemarkMatch = (t.licenses || []).some(l => l.remark && String(l.remark).toLowerCase().includes(q));
        const licTenantIdMatch = (t.licenses || []).some(l => l.tenant_id !== null && l.tenant_id !== undefined && String(l.tenant_id).toLowerCase().includes(q));
        const licTenantNameMatch = (t.licenses || []).some(l => l.tenant_name && String(l.tenant_name).toLowerCase().includes(q));

        return (
          tenantName.includes(q) ||
          tenantId.includes(q) ||
          setupName.includes(q) ||
          accSt.includes(q) ||
          licSt.includes(q) ||
          remark.includes(q) ||
          licRemarkMatch ||
          licTenantIdMatch ||
          licTenantNameMatch
        );
      });
    }

    // Sort standalone list
    list = [...list].sort((a, b) => {
      if (sortConfig.key === 'tenant_name') {
        const nameA = a.tenant_name ? a.tenant_name.toLowerCase() : null;
        const nameB = b.tenant_name ? b.tenant_name.toLowerCase() : null;
        if (nameA === null && nameB === null) return 0;
        if (nameA === null) return 1;
        if (nameB === null) return -1;
        const comparison = nameA.localeCompare(nameB);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      if (sortConfig.key === 'tenant_id') {
        const idA = a.first_tenant_id_num;
        const idB = b.first_tenant_id_num;
        if (idA === null && idB === null) return 0;
        if (idA === null) return 1;
        if (idB === null) return -1;
        return sortConfig.direction === 'asc' ? idA - idB : idB - idA;
      }

      if (sortConfig.key === 'start_date') {
        const timeA = a.minStartDate ? a.minStartDate.getTime() : 0;
        const timeB = b.minStartDate ? b.minStartDate.getTime() : 0;
        return sortConfig.direction === 'asc' ? timeA - timeB : timeB - timeA;
      }

      if (sortConfig.key === 'end_date') {
        const timeA = a.maxEndDate ? a.maxEndDate.getTime() : 0;
        const timeB = b.maxEndDate ? b.maxEndDate.getTime() : 0;
        return sortConfig.direction === 'asc' ? timeA - timeB : timeB - timeA;
      }

      if (sortConfig.key === 'license_status') {
        const stA = a.license_status || '';
        const stB = b.license_status || '';
        const comparison = stA.localeCompare(stB);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      return 0;
    });

    return list;
  }, [allGroupedTenants, selectedSetupFilter, selectedTenantFilter, accStatusTab, searchQuery, sortConfig]);

  // Filtered raw licenses for embedded view
  const filteredLicenses = useMemo(() => {
    let list = licenseInfos;

    if (setupId) {
      list = list.filter(info => info.setup_id === setupId);
    }

    if (autoOpenLicenseId) {
      list = list.filter(info => info.id === autoOpenLicenseId || info.license_id === autoOpenLicenseId);
    }

    if (accStatusTab !== 'all') {
      list = list.filter(info => getItemAccStatus(info) === accStatusTab);
    }

    if (searchQuery && searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((info) => {
        const tenantName = (info.tenant_name || '').toLowerCase();
        const tenantId = info.tenant_id !== null && info.tenant_id !== undefined ? String(info.tenant_id).toLowerCase() : '';
        const setupName = (getSetupName(info) || '').toLowerCase();
        const accSt = getItemAccStatus(info);
        const remark = (info.remark || '').toLowerCase();
        const reason = (info.status_reason || '').toLowerCase();
        return (
          tenantName.includes(q) ||
          tenantId.includes(q) ||
          setupName.includes(q) ||
          accSt.includes(q) ||
          remark.includes(q) ||
          reason.includes(q)
        );
      });
    }

    return list;
  }, [licenseInfos, setupId, autoOpenLicenseId, accStatusTab, searchQuery, setupInfos]);

  const pageSize = 15;
  const currentList = isEmbedded ? filteredLicenses : filteredTenants;
  const pageCount = Math.ceil(currentList.length / pageSize) || 1;
  const safeCurrentPage = currentPage >= pageCount ? 0 : currentPage;
  const displayedLicenses = isEmbedded ? filteredLicenses.slice(safeCurrentPage * pageSize, (safeCurrentPage + 1) * pageSize) : [];
  const displayedTenants = !isEmbedded ? filteredTenants.slice(safeCurrentPage * pageSize, (safeCurrentPage + 1) * pageSize) : [];

  const renderStatusTabs = () => {
    const tabs = [
      { key: 'all', label: 'All', count: statusCounts.all },
      { key: 'active', label: 'Active', count: statusCounts.active },
      { key: 'deactivated', label: 'Deactivated', count: statusCounts.deactivated },
      { key: 'suspended', label: 'Suspended', count: statusCounts.suspended },
    ];

    return (
      <div className="d-flex align-items-center flex-wrap gap-2">
        {tabs.map((tab) => {
          const isActive = accStatusTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => { setAccStatusTab(tab.key); setCurrentPage(0); }}
              className="btn btn-sm d-inline-flex align-items-center px-3 py-1.5 rounded-pill fw-medium border shadow-none"
              style={{
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                backgroundColor: isActive ? '#e0f2fe' : '#ffffff',
                color: isActive ? '#0369a1' : '#64748b',
                borderColor: isActive ? '#bae6fd' : '#e2e8f0',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }
              }}
            >
              <span>{tab.label}</span>
              <span
                className="badge rounded-pill ms-2 px-2 py-0.5"
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  minWidth: '22px',
                  backgroundColor: isActive ? '#0284c7' : '#f1f5f9',
                  color: isActive ? '#ffffff' : '#64748b',
                  border: isActive ? 'none' : '1px solid #e2e8f0',
                }}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderTableContent = () => (
    <>
      {isEmbedded ? (
        displayedLicenses.length > 0 ? (
          displayedLicenses.map((info, index) => {
            const rowClass = getRowColorClass(info);
            const displayValidity = info.extended_validity || info.validity;
            const activeStatus = getLicenseStatus(info);

            return (
              <tr key={info.id}>
                <td className={rowClass}>{(safeCurrentPage * 15) + index + 1}</td>
                <td 
                  className={`${rowClass} text-primary`}
                  onClick={() => { 
                      setCurrentRecord(info); 
                      setShowDetailsModal(true); 
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex justify-content-center">
                    <div style={{ width: '150px', textAlign: 'left' }}>
                      {info.remark ? (
                        <span title={info.remark}>
                          {info.remark.length > 25 ? `${info.remark.substring(0, 25)}...` : info.remark}
                        </span>
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                </td>
                <td className={rowClass}>{info.tenant_id ?? '-'}</td>
                <td className={rowClass}>{info.start_date ? dayjs(info.start_date).format('DD-MM-YYYY') : ''}</td>
                <td className={rowClass}>{displayValidity ? dayjs(displayValidity).format('DD-MM-YYYY') : ''}</td>
                <td className={rowClass}>
                  {(() => {
                    if (!displayValidity) return '-';
                    const validityDate = parseValidDate(displayValidity);
                    if (!validityDate) return '-';
                    validityDate.setHours(0, 0, 0, 0);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const diffTime = validityDate - today;
                    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                    
                    if (diffDays < 0) return 'Expired';
                    if (diffDays === 0) return 'Expires Today';
                    return `${diffDays} Days`;
                  })()}
                </td>
                <td>
                  {(() => {
                    let badgeClass = 'bg-success';
                    if (activeStatus === 'Expired') {
                      badgeClass = 'bg-danger';
                    } else if (activeStatus === 'Expired in 7 days') {
                      badgeClass = 'bg-soft-danger text-danger border border-danger';
                    } else if (activeStatus === 'Extended') {
                      badgeClass = 'bg-soft-success text-success border border-success';
                    }
                    return (
                      <span className={`badge ${badgeClass} text-capitalize`}>
                        {activeStatus}
                      </span>
                    );
                  })()}
                </td>
                <td>
                  <div className="dropdown">
                    <i
                      className="mdi mdi-dots-horizontal font-size-18"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      style={{ cursor: 'pointer' }}
                    ></i>
                    <ul className="dropdown-menu dropdown-menu-end font-size-14">
                      <li>
                        <button className="dropdown-item text-primary" onClick={() => { 
                            setCurrentRecord(info); 
                            setShowDetailsModal(true); 
                        }}>View Details</button>
                      </li>
                      <li>
                        <button className="dropdown-item text-success" onClick={() => { 
                            setCurrentRecord(info); 
                            setShowExtendModal(true); 
                        }}>Extend Date</button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={() => handleShowModal(info)}>Edit</button>
                      </li>
                      <li>
                        <button className="dropdown-item text-danger" onClick={() => handleDelete(info.id)}>Delete</button>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan="8" className="text-center">
              {accStatusTab !== 'all' ? `No ${accStatusTab} licenses found` : 'No License Info available'}
            </td>
          </tr>
        )
      ) : (
        displayedTenants.length > 0 ? (
          displayedTenants.map((tenant, index) => {
            const rowNum = (safeCurrentPage * pageSize) + index + 1;
            const isNull = tenant.isNull || tenant.tenant_name === null;
            const st = tenant.acc_status;
            let badgeCls = 'bg-soft-success text-success border border-success';
            if (st === 'suspended') badgeCls = 'bg-soft-warning text-warning border border-warning';
            else if (st === 'deactivated') badgeCls = 'bg-soft-danger text-danger border border-danger';

            return (
              <tr key={tenant.groupKey}>
                <td>{rowNum}</td>
                <td
                  className="fw-medium text-dark"
                  onClick={() => handleOpenTenantDetails(tenant)}
                  style={{ cursor: 'pointer', color: '#000000' }}
                  title={isNull ? 'null' : tenant.tenant_name}
                >
                  <div className="d-flex justify-content-center">
                    <div style={{ width: '150px', textAlign: 'left', color: '#000000' }}>
                      {isNull ? (
                        <span className="text-muted fw-semibold">null</span>
                      ) : (
                        <span className="text-break text-dark fw-medium" style={{ color: '#000000' }}>{tenant.tenant_name}</span>
                      )}
                    </div>
                  </div>
                </td>
                <td title={tenant.setup_name}>
                  <div className="d-flex justify-content-center">
                    <div style={{ width: '150px', textAlign: 'left' }}>
                      <span className="text-break">{tenant.setup_name}</span>
                    </div>
                  </div>
                </td>
                <td>{tenant.tenant_id !== null && tenant.tenant_id !== undefined ? tenant.tenant_id : 'null'}</td>
                <td className="text-nowrap">{tenant.startDateFormatted || '-'}</td>
                <td className="text-nowrap">{tenant.endDateFormatted || '-'}</td>
                <td>
                  {(() => {
                    const ls = tenant.license_status || 'Active';
                    let badgeClass = 'bg-success';
                    if (ls === 'Expired') {
                      badgeClass = 'bg-danger';
                    } else if (ls === 'Expired in 7 days') {
                      badgeClass = 'bg-soft-danger text-danger border border-danger';
                    } else if (ls === 'Extended') {
                      badgeClass = 'bg-soft-success text-success border border-success';
                    }
                    return (
                      <span className={`badge ${badgeClass} text-capitalize`}>
                        {ls}
                      </span>
                    );
                  })()}
                </td>
                <td>
                  <span className={`badge ${badgeCls} text-capitalize`}>
                    {st}
                  </span>
                </td>
                <td>
                  <div className="dropdown">
                    <i
                      className="mdi mdi-dots-horizontal font-size-18"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      style={{ cursor: 'pointer' }}
                    ></i>
                    <ul className="dropdown-menu dropdown-menu-end font-size-14">
                      <li>
                        <button
                          className="dropdown-item text-primary"
                          onClick={() => handleOpenTenantDetails(tenant)}
                        >
                          View Details
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handleShowModal({
                            tenant_name: tenant.isNull ? '' : tenant.tenant_name,
                            tenant_id: tenant.tenant_id !== null && tenant.tenant_id !== undefined ? tenant.tenant_id : '',
                            setup_id: tenant.primarySetupId || ''
                          })}
                        >
                          Add License
                        </button>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan="9" className="text-center">
              {accStatusTab !== 'all' ? `No ${accStatusTab} tenants found` : 'No Tenant Info available'}
            </td>
          </tr>
        )
      )}
    </>
  );

  const tableHeader = isEmbedded ? (
    <tr>
      <th className="col">Sl No</th>
      <th className="col">
        <div className="d-flex justify-content-center">
          <div style={{ width: '150px', textAlign: 'left' }}>
            Remark
          </div>
        </div>
      </th>
      <th className="col">Tenant ID</th>
      <th className="col">Start Date</th>
      <th className="col">Validity</th>
      <th className="col">Days Remaining</th>
      <th className="col">Status</th>
      <th className="col">Actions</th>
    </tr>
  ) : (
    <tr>
      <th className="align-middle">ID</th>
      <th 
        className="align-middle"
        onClick={() => handleSort('tenant_name')}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title={`Sort by Tenant Name (${sortConfig.key === 'tenant_name' && sortConfig.direction === 'asc' ? 'Descending' : 'Ascending'})`}
      >
        <div className="d-flex justify-content-center">
          <div style={{ width: '150px', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
            <span>Tenant Name</span>
            <i 
              className={`mdi ${
                sortConfig.key === 'tenant_name' 
                  ? (sortConfig.direction === 'asc' ? 'mdi-arrow-up text-primary' : 'mdi-arrow-down text-primary') 
                  : 'mdi-swap-vertical text-muted opacity-50'
              } font-size-13 ms-1`}
            ></i>
            <Dropdown 
              className="d-inline-block ms-1" 
              autoClose="outside"
              onClick={(e) => e.stopPropagation()}
            >
              <Dropdown.Toggle 
                as="span" 
                style={{ cursor: 'pointer', verticalAlign: 'middle' }} 
                className="p-1"
                title={selectedTenantFilter ? `Filtered: ${selectedTenantFilter.label}` : "Filter Tenant"}
              >
                <i 
                  className={`mdi ${selectedTenantFilter ? 'mdi-filter text-primary' : 'mdi-filter-outline text-muted'}`} 
                  style={{ fontSize: '15px' }}
                ></i>
              </Dropdown.Toggle>

              <Dropdown.Menu 
                className="shadow-lg border-0 rounded-3 p-2" 
                style={{ minWidth: '240px', maxWidth: '300px', zIndex: 1060 }}
              >
                <div className="px-1 py-1">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-semibold text-dark font-size-12">Filter Tenant</span>
                    {selectedTenantFilter && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-danger font-size-11 text-decoration-none"
                        onClick={() => {
                          setSelectedTenantFilter(null);
                          setCurrentPage(0);
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="position-relative mb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm pe-4"
                      placeholder="Search tenant..."
                      value={tenantSearchInput}
                      onChange={(e) => setTenantSearchInput(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                    {tenantSearchInput && (
                      <i 
                        className="mdi mdi-close position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
                        style={{ cursor: 'pointer', fontSize: '14px' }}
                        onClick={() => setTenantSearchInput('')}
                      ></i>
                    )}
                  </div>
                </div>
                
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <button
                    type="button"
                    className={`dropdown-item rounded py-1.5 px-2 font-size-12 d-flex align-items-center justify-content-between ${!selectedTenantFilter ? 'active bg-primary text-white' : ''}`}
                    onClick={() => {
                      setSelectedTenantFilter(null);
                      setCurrentPage(0);
                    }}
                  >
                    <span>All Tenants</span>
                    {!selectedTenantFilter && <i className="mdi mdi-check"></i>}
                  </button>

                  {filteredTenantOptions.length > 0 ? (
                    filteredTenantOptions.map((opt) => {
                      const isSelected = selectedTenantFilter?.value === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className={`dropdown-item rounded py-1.5 px-2 font-size-12 d-flex align-items-center justify-content-between text-truncate ${isSelected ? 'active bg-primary text-white' : ''}`}
                          title={opt.label}
                          onClick={() => {
                            setSelectedTenantFilter(opt);
                            setCurrentPage(0);
                          }}
                        >
                          <span className="text-truncate me-2">{opt.label}</span>
                          {isSelected && <i className="mdi mdi-check flex-shrink-0"></i>}
                        </button>
                      );
                    })
                  ) : (
                    <div className="text-muted text-center py-2 font-size-12">
                      No tenants found
                    </div>
                  )}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </th>
      <th className="align-middle">
        <div className="d-flex justify-content-center">
          <div style={{ width: '150px', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
            <span>Setup Name</span>
            <Dropdown 
              className="d-inline-block ms-1" 
              autoClose="outside"
              onClick={(e) => e.stopPropagation()}
            >
              <Dropdown.Toggle 
                as="span" 
                style={{ cursor: 'pointer', verticalAlign: 'middle' }} 
                className="p-1"
                title={selectedSetupFilter ? `Filtered: ${selectedSetupFilter.label}` : "Filter Setup"}
              >
                <i 
                  className={`mdi ${selectedSetupFilter ? 'mdi-filter text-primary' : 'mdi-filter-outline text-muted'}`} 
                  style={{ fontSize: '15px' }}
                ></i>
              </Dropdown.Toggle>

              <Dropdown.Menu 
                className="shadow-lg border-0 rounded-3 p-2" 
                style={{ minWidth: '240px', maxWidth: '300px', zIndex: 1060 }}
              >
                <div className="px-1 py-1">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-semibold text-dark font-size-12">Filter Setup</span>
                    {selectedSetupFilter && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-danger font-size-11 text-decoration-none"
                        onClick={() => {
                          setSelectedSetupFilter(null);
                          setCurrentPage(0);
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="position-relative mb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm pe-4"
                      placeholder="Search setup..."
                      value={setupSearchInput}
                      onChange={(e) => setSetupSearchInput(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                    {setupSearchInput && (
                      <i 
                        className="mdi mdi-close position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
                        style={{ cursor: 'pointer', fontSize: '14px' }}
                        onClick={() => setSetupSearchInput('')}
                      ></i>
                    )}
                  </div>
                </div>
                
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <button
                    type="button"
                    className={`dropdown-item rounded py-1.5 px-2 font-size-12 d-flex align-items-center justify-content-between ${!selectedSetupFilter ? 'active bg-primary text-white' : ''}`}
                    onClick={() => {
                      setSelectedSetupFilter(null);
                      setCurrentPage(0);
                    }}
                  >
                    <span>All Setups</span>
                    {!selectedSetupFilter && <i className="mdi mdi-check"></i>}
                  </button>

                  {filteredSetupOptions.length > 0 ? (
                    filteredSetupOptions.map((opt) => {
                      const isSelected = selectedSetupFilter?.value === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className={`dropdown-item rounded py-1.5 px-2 font-size-12 d-flex align-items-center justify-content-between text-truncate ${isSelected ? 'active bg-primary text-white' : ''}`}
                          title={opt.label}
                          onClick={() => {
                            setSelectedSetupFilter(opt);
                            setCurrentPage(0);
                            if (selectedTenantFilter) {
                              const belongs = licenseInfos.some(
                                l => String(l.setup_id) === String(opt.value) && 
                                     l.tenant_name && 
                                     l.tenant_name.toLowerCase().trim() === selectedTenantFilter.value.toLowerCase().trim()
                              );
                              if (!belongs) {
                                setSelectedTenantFilter(null);
                              }
                            }
                          }}
                        >
                          <span className="text-truncate me-2">{opt.label}</span>
                          {isSelected && <i className="mdi mdi-check flex-shrink-0"></i>}
                        </button>
                      );
                    })
                  ) : (
                    <div className="text-muted text-center py-2 font-size-12">
                      No setups found
                    </div>
                  )}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </th>
      <th 
        className="align-middle"
        onClick={() => handleSort('tenant_id')}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title={`Sort by Tenant ID (${sortConfig.key === 'tenant_id' && sortConfig.direction === 'asc' ? 'Descending' : 'Ascending'})`}
      >
        <div className="d-inline-flex align-items-center">
          <span>Tenant ID</span>
          <i 
            className={`mdi ${
              sortConfig.key === 'tenant_id' 
                ? (sortConfig.direction === 'asc' ? 'mdi-arrow-up text-primary' : 'mdi-arrow-down text-primary') 
                : 'mdi-swap-vertical text-muted opacity-50'
            } font-size-13 ms-1`}
          ></i>
        </div>
      </th>
      <th 
        className="align-middle"
        onClick={() => handleSort('start_date')}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title={`Sort by Start Date (${sortConfig.key === 'start_date' && sortConfig.direction === 'asc' ? 'Descending' : 'Ascending'})`}
      >
        <div className="d-inline-flex align-items-center">
          <span>Start Date</span>
          <i 
            className={`mdi ${
              sortConfig.key === 'start_date' 
                ? (sortConfig.direction === 'asc' ? 'mdi-arrow-up text-primary' : 'mdi-arrow-down text-primary') 
                : 'mdi-swap-vertical text-muted opacity-50'
            } font-size-13 ms-1`}
          ></i>
        </div>
      </th>
      <th 
        className="align-middle"
        onClick={() => handleSort('end_date')}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title={`Sort by End Date (${sortConfig.key === 'end_date' && sortConfig.direction === 'asc' ? 'Descending' : 'Ascending'})`}
      >
        <div className="d-inline-flex align-items-center">
          <span>End Date</span>
          <i 
            className={`mdi ${
              sortConfig.key === 'end_date' 
                ? (sortConfig.direction === 'asc' ? 'mdi-arrow-up text-primary' : 'mdi-arrow-down text-primary') 
                : 'mdi-swap-vertical text-muted opacity-50'
            } font-size-13 ms-1`}
          ></i>
        </div>
      </th>
      <th 
        className="align-middle"
        onClick={() => handleSort('license_status')}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title={`Sort by License Status (${sortConfig.key === 'license_status' && sortConfig.direction === 'asc' ? 'Descending' : 'Ascending'})`}
      >
        <div className="d-inline-flex align-items-center">
          <span>License Status</span>
          <i 
            className={`mdi ${
              sortConfig.key === 'license_status' 
                ? (sortConfig.direction === 'asc' ? 'mdi-arrow-up text-primary' : 'mdi-arrow-down text-primary') 
                : 'mdi-swap-vertical text-muted opacity-50'
            } font-size-13 ms-1`}
          ></i>
        </div>
      </th>
      <th className="align-middle">Acc Status</th>
      <th className="align-middle">Actions</th>
    </tr>
  );

  const paginator = pageCount > 1 && (
    <div className="d-flex justify-content-end mt-3">
      <ReactPaginate
        previousLabel={"<"}
        nextLabel={">"}
        breakLabel={"..."}
        pageCount={pageCount}
        marginPagesDisplayed={2}
        pageRangeDisplayed={3}
        onPageChange={(data) => setCurrentPage(data.selected)}
        forcePage={safeCurrentPage}
        containerClassName={"pagination pagination-rounded"}
        pageClassName={"page-item"}
        pageLinkClassName={"page-link"}
        previousClassName={"page-item"}
        previousLinkClassName={"page-link"}
        nextClassName={"page-item"}
        nextLinkClassName={"page-link"}
        breakClassName={"page-item"}
        breakLinkClassName={"page-link"}
        activeClassName={"active"}
      />
    </div>
  );

  const embeddedContent = (
    <div>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 mt-2">
        <div>
          {renderStatusTabs()}
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="search-box">
            <div className="position-relative">
              <input
                type="text"
                className="form-control"
                placeholder="Search license info..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <i className="bx bx-search-alt search-icon"></i>
            </div>
          </div>
          <Dropdown>
            <Dropdown.Toggle as={CustomToggle} />
            <Dropdown.Menu className="shadow-lg border-0 rounded-3">
              <Dropdown.Header className="fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '1px' }}>Filter by Expiry</Dropdown.Header>
              <Dropdown.Item active={expiryFilter === ''} onClick={() => handleExpiryFilterChange({target: {value: ''}})}>All Licenses</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item active={expiryFilter === 'expired'} onClick={() => handleExpiryFilterChange({target: {value: 'expired'}})}>
                <span className="text-danger fw-bold"><i className="mdi mdi-alert-circle-outline me-1"></i>Expired</span>
              </Dropdown.Item>
              <Dropdown.Item active={expiryFilter === 'expired_7_days'} onClick={() => handleExpiryFilterChange({target: {value: 'expired_7_days'}})}>
                <span className="text-danger"><i className="mdi mdi-clock-alert-outline me-1"></i>Expired in 7 days</span>
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item active={expiryFilter === '1_day'} onClick={() => handleExpiryFilterChange({target: {value: '1_day'}})}>
                <span className="text-warning"><i className="mdi mdi-timer-sand me-1"></i>Expiring in 1 Day</span>
              </Dropdown.Item>
              <Dropdown.Item active={expiryFilter === '7_days'} onClick={() => handleExpiryFilterChange({target: {value: '7_days'}})}>
                <span className="text-warning"><i className="mdi mdi-calendar-alert me-1"></i>Expiring in 7 Days</span>
              </Dropdown.Item>
              <Dropdown.Item active={expiryFilter === '30_days'} onClick={() => handleExpiryFilterChange({target: {value: '30_days'}})}>
                <span className="text-info"><i className="mdi mdi-calendar-clock me-1"></i>Expiring in 30 Days</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <button
            type="button"
            className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button shadow-sm"
            onClick={() => handleShowModal()}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Add License Info"
          >
            <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
          </button>
        </div>
      </div>
      <div className="contacts-table-modern">
        <div className="table-responsive" style={{ overflow: 'visible', minHeight: '200px', paddingBottom: '100px' }}>
          <table className="table table-modern">
            <thead>
              {tableHeader}
            </thead>
            <tbody>
              {renderTableContent()}
            </tbody>
          </table>
        </div>
        {paginator}
      </div>
    </div>
  );

  const innerContent = isEmbedded ? embeddedContent : (
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div>
                      {renderStatusTabs()}
                    </div>
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      {(selectedSetupFilter || selectedTenantFilter) && (
                        <div className="d-flex align-items-center gap-1">
                          {selectedSetupFilter && (
                            <span className="badge bg-light text-primary border d-flex align-items-center gap-1 py-1 px-2 font-size-12">
                              Setup: {selectedSetupFilter.label}
                              <i 
                                className="mdi mdi-close cursor-pointer ms-1 text-danger"
                                style={{ cursor: 'pointer' }}
                                onClick={() => { setSelectedSetupFilter(null); setCurrentPage(0); }}
                                title="Remove Setup filter"
                              ></i>
                            </span>
                          )}
                          {selectedTenantFilter && (
                            <span className="badge bg-light text-primary border d-flex align-items-center gap-1 py-1 px-2 font-size-12">
                              Tenant: {selectedTenantFilter.label}
                              <i 
                                className="mdi mdi-close cursor-pointer ms-1 text-danger"
                                style={{ cursor: 'pointer' }}
                                onClick={() => { setSelectedTenantFilter(null); setCurrentPage(0); }}
                                title="Remove Tenant filter"
                              ></i>
                            </span>
                          )}
                        </div>
                      )}
                      <div className="search-box">
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search license info..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                          />
                          <i className="bx bx-search-alt search-icon"></i>
                        </div>
                      </div>
                      <Dropdown>
                        <Dropdown.Toggle as={CustomToggle} />
                        <Dropdown.Menu className="shadow-lg border-0 rounded-3">
                          <Dropdown.Header className="fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '1px' }}>Filter by Expiry</Dropdown.Header>
                          <Dropdown.Item active={expiryFilter === ''} onClick={() => handleExpiryFilterChange({target: {value: ''}})}>All Licenses</Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item active={expiryFilter === 'expired'} onClick={() => handleExpiryFilterChange({target: {value: 'expired'}})}>
                            <span className="text-danger fw-bold"><i className="mdi mdi-alert-circle-outline me-1"></i>Expired</span>
                          </Dropdown.Item>
                          <Dropdown.Item active={expiryFilter === 'expired_7_days'} onClick={() => handleExpiryFilterChange({target: {value: 'expired_7_days'}})}>
                            <span className="text-danger"><i className="mdi mdi-clock-alert-outline me-1"></i>Expired in 7 days</span>
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item active={expiryFilter === '1_day'} onClick={() => handleExpiryFilterChange({target: {value: '1_day'}})}>
                            <span className="text-warning"><i className="mdi mdi-timer-sand me-1"></i>Expiring in 1 Day</span>
                          </Dropdown.Item>
                          <Dropdown.Item active={expiryFilter === '7_days'} onClick={() => handleExpiryFilterChange({target: {value: '7_days'}})}>
                            <span className="text-warning"><i className="mdi mdi-calendar-alert me-1"></i>Expiring in 7 Days</span>
                          </Dropdown.Item>
                          <Dropdown.Item active={expiryFilter === '30_days'} onClick={() => handleExpiryFilterChange({target: {value: '30_days'}})}>
                            <span className="text-info"><i className="mdi mdi-calendar-clock me-1"></i>Expiring in 30 Days</span>
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                      {(selectedSetupFilter || selectedTenantFilter || expiryFilter) && (
                        <button
                          type="button"
                          className="btn btn-light border d-flex align-items-center justify-content-center shadow-sm text-danger px-2"
                          style={{ height: '38px' }}
                          onClick={() => {
                            setSelectedSetupFilter(null);
                            setSelectedTenantFilter(null);
                            setExpiryFilter('');
                            setCurrentPage(0);
                          }}
                          title="Reset Filters"
                        >
                          <i className="mdi mdi-filter-remove font-size-16"></i>
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button shadow-sm"
                        onClick={() => handleShowModal()}
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Add License Info"
                      >
                        <i className="mdi mdi-plus"></i>
                      </button>
                    </div>
                  </div>
                  <div >
                    <table className="table align-middle table-nowrap table-check table-list">
                      <thead className="table-sales-gradient">
                        {tableHeader}
                      </thead>
                      <tbody>
                        {renderTableContent()}
                      </tbody>
                    </table>
                  </div>
                  {paginator}
                </div>
              </div>
            </div>
          </div>
  );

  const licenseModal = (
    <Offcanvas show={showModal} onHide={handleCloseModal} placement="end" style={{ width: isEmbedded ? '1180px' : '930px', zIndex: 1055 }} className="custom-offcanvas">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="fw-bold text-primary">{currentRecord ? 'Edit License Info' : 'Add License Info'}</Offcanvas.Title>
      </Offcanvas.Header>
      <hr style={{ margin: 0 }} />
      <Offcanvas.Body>
        <Form noValidate onSubmit={handleSubmit}>
          <h5 className="mb-3 text-secondary">Basic Info</h5>
          <Row>
            {!isEmbedded ? (
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label form-font">
                    Setup <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select 
                    className={`form-feild ${errors?.setup_id ? 'is-invalid border-danger' : ''}`} 
                    name="setup_id" 
                    defaultValue={currentRecord?.setup_id || ''}
                    onChange={() => setErrors(prev => ({ ...prev, setup_id: null }))}
                  >
                    <option value="">Select Setup</option>
                    {setupInfos.map(setup => (
                      <option key={setup.id} value={setup.id}>{setup.client_name || setup.key}</option>
                    ))}
                  </Form.Select>
                  {errors?.setup_id && (
                    <div className="text-danger font-size-11 mt-1 fw-bold">{errors.setup_id}</div>
                  )}
                </Form.Group>
              </Col>
            ) : (
              <input type="hidden" name="setup_id" value={currentRecord?.setup_id || setupId || ''} />
            )}

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Tenant Name</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="text" 
                  name="tenant_name" 
                  placeholder="Enter Tenant Name"
                  defaultValue={currentRecord?.tenant_name || ''} 
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Tenant ID</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="number" 
                  name="tenant_id" 
                  placeholder="Enter Tenant ID"
                  defaultValue={currentRecord?.tenant_id || ''} 
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Account Status</Form.Label>
                <Form.Select 
                  className="form-feild text-capitalize" 
                  name="acc_status" 
                  defaultValue={currentRecord?.acc_status ? String(currentRecord.acc_status).toLowerCase() : 'active'}
                >
                  <option value="active">Active</option>
                  <option value="deactivated">Deactivated</option>
                  <option value="suspended">Suspended</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={!isEmbedded ? 12 : 6}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Status Reason</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="text" 
                  name="status_reason" 
                  placeholder="Enter reason for account status"
                  defaultValue={currentRecord?.status_reason || ''} 
                />
              </Form.Group>
            </Col>
          </Row>

          <h5 className="mb-3 text-secondary mt-2">Dates</h5>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3 w-100">
                <Form.Label className="form-label form-font">
                  Start Date <span className="text-danger">*</span>
                </Form.Label>
                <DatePicker
                  selected={modalStartDate}
                  onChange={(date) => {
                    setModalStartDate(date);
                    setErrors(prev => ({ ...prev, start_date: null }));
                    if (modalValidityDate && date && modalValidityDate < date) {
                      setModalValidityDate(date);
                    }
                  }}
                  className={`form-control form-feild w-100 ${errors?.start_date ? 'is-invalid border border-danger' : ''}`}
                  wrapperClassName="w-100"
                  dateFormat="dd-MM-yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText="Select Start Date"
                  name="start_date"
                />
                {errors?.start_date && (
                  <div className="text-danger font-size-11 mt-1 fw-bold">{errors.start_date}</div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3 w-100">
                <Form.Label className="form-label form-font">
                  Validity <span className="text-danger">*</span>
                </Form.Label>
                <DatePicker
                  selected={modalValidityDate}
                  onChange={(date) => {
                    setModalValidityDate(date);
                    setErrors(prev => ({ ...prev, validity: null }));
                  }}
                  className={`form-control form-feild w-100 ${errors?.validity ? 'is-invalid border border-danger' : ''}`}
                  wrapperClassName="w-100"
                  dateFormat="dd-MM-yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText="Select Validity Date"
                  name="validity"
                  minDate={modalStartDate}
                />
                {errors?.validity && (
                  <div className="text-danger font-size-11 mt-1 fw-bold">{errors.validity}</div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <h5 className="mb-3 text-secondary mt-2">Licenses</h5>
          <Row>
           
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Super Admin</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="number" 
                  name="role_s_admin" 
                  value={rolesState.role_s_admin || ''} 
                  onChange={(e) => handleRoleChange('role_s_admin', e.target.value)} 
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Admin</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="number" 
                  name="role_admin" 
                  value={rolesState.role_admin || ''} 
                  onChange={(e) => handleRoleChange('role_admin', e.target.value)} 
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Supervisor</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="number" 
                  name="role_supervisor" 
                  value={rolesState.role_supervisor || ''} 
                  onChange={(e) => handleRoleChange('role_supervisor', e.target.value)} 
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Teamlead</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="number" 
                  name="role_teamlead" 
                  value={rolesState.role_teamlead || ''} 
                  onChange={(e) => handleRoleChange('role_teamlead', e.target.value)} 
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Analyst</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="number" 
                  name="role_analyst" 
                  value={rolesState.role_analyst || ''} 
                  onChange={(e) => handleRoleChange('role_analyst', e.target.value)} 
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">C. Agent</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="number" 
                  name="role_cagent" 
                  value={rolesState.role_cagent || ''} 
                  onChange={(e) => handleRoleChange('role_cagent', e.target.value)} 
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">V. Agent</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="number" 
                  name="role_vagent" 
                  value={rolesState.role_vagent || ''} 
                  onChange={(e) => handleRoleChange('role_vagent', e.target.value)} 
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Ticket Agent</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="number" 
                  name="role_tagent" 
                  value={rolesState.role_tagent || ''} 
                  onChange={(e) => handleRoleChange('role_tagent', e.target.value)} 
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Omni Agent</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="number" 
                  name="role_agent" 
                  value={rolesState.role_agent || ''} 
                  onChange={(e) => handleRoleChange('role_agent', e.target.value)} 
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Agent Plus</Form.Label>
                <Form.Control 
                  className="form-feild" 
                  type="number" 
                  name="role_agent_plus" 
                  value={rolesState.role_agent_plus || ''} 
                  onChange={(e) => handleRoleChange('role_agent_plus', e.target.value)} 
                  min="0"
                />
              </Form.Group>
            </Col>
             <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">User Concurrency</Form.Label>
                <Form.Select 
                  className="form-feild" 
                  name="user_concurrency" 
                  value={userConcurrency}
                  onChange={(e) => setUserConcurrency(e.target.value)}
                >
                  <option value="1:1">1:1</option>
                  <option value="2:1">2:1</option>
                  <option value="3:1">3:1</option>
                </Form.Select>
              </Form.Group>
            </Col>
            {/* <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">User Login (Total Roles)</Form.Label>
                <Form.Control 
                  className="form-feild bg-light text-muted fw-bold" 
                  type="number" 
                  name="user_login" 
                  value={calculatedUserLogin} 
                  readOnly 
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">User Create (Calculated)</Form.Label>
                <Form.Control 
                  className="form-feild bg-light text-muted fw-bold" 
                  type="number" 
                  name="user_create" 
                  value={calculatedUserCreate} 
                  readOnly 
                />
              </Form.Group>
            </Col> */}
          </Row>

          <h5 className="mb-3 text-secondary mt-2">Modules & Features</h5>
          <Row>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Email</Form.Label><Form.Control className="form-feild" type="number" name="module_email" defaultValue={currentRecord?.module_email ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Voicemail</Form.Label><Form.Control className="form-feild" type="number" name="module_voicemail" defaultValue={currentRecord?.module_voicemail ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Chat</Form.Label><Form.Control className="form-feild" type="number" name="module_chat" defaultValue={currentRecord?.module_chat ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Interaction</Form.Label><Form.Control className="form-feild" type="number" name="module_interaction" defaultValue={currentRecord?.module_interaction ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Voice</Form.Label><Form.Control className="form-feild" type="number" name="module_voice" defaultValue={currentRecord?.module_voice ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Video</Form.Label><Form.Control className="form-feild" type="number" name="module_video" defaultValue={currentRecord?.module_video ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">API</Form.Label><Form.Control className="form-feild" type="number" name="module_api" defaultValue={currentRecord?.module_api ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Ticket</Form.Label><Form.Control className="form-feild" type="number" name="module_ticket" defaultValue={currentRecord?.module_ticket ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Camp Voice</Form.Label><Form.Control className="form-feild" type="number" name="camp_voice" defaultValue={currentRecord?.camp_voice ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Camp Waba</Form.Label><Form.Control className="form-feild" type="number" name="camp_waba" defaultValue={currentRecord?.camp_waba ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Camp SMS</Form.Label><Form.Control className="form-feild" type="number" name="camp_sms" defaultValue={currentRecord?.camp_sms ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Camp Email</Form.Label><Form.Control className="form-feild" type="number" name="camp_email" defaultValue={currentRecord?.camp_email ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Mobile App</Form.Label><Form.Control className="form-feild" type="number" name="mobile_app" defaultValue={currentRecord?.mobile_app ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Wallboard</Form.Label><Form.Control className="form-feild" type="number" name="wallboard" defaultValue={currentRecord?.wallboard ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Gen AI</Form.Label><Form.Control className="form-feild" type="number" name="gen_ai" defaultValue={currentRecord?.gen_ai ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Toolbar App</Form.Label><Form.Control className="form-feild" type="number" name="toolbar_app" defaultValue={currentRecord?.toolbar_app ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">Toolbar App Support</Form.Label><Form.Control className="form-feild" type="number" name="toolbar_app_sup" defaultValue={currentRecord?.toolbar_app_sup ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">AI Assist</Form.Label><Form.Control className="form-feild" type="number" name="ai_assist" defaultValue={currentRecord?.ai_assist ?? ''} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label className="form-label form-font">AI Pro</Form.Label><Form.Control className="form-feild" type="number" name="ai_assist_pro" defaultValue={currentRecord?.ai_assist_pro ?? ''} /></Form.Group></Col>
          </Row>

          <h5 className="mb-3 text-secondary mt-2">IP & Remarks</h5>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3 mt-4">
                <Form.Check  type="checkbox" label={<span className="form-label form-font">Enable IP Check</span>} name="ip_check" defaultChecked={currentRecord?.ip_check || false} />
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">IP List </Form.Label>
                <Form.Control className="form-feild" type="text" name="ips" defaultValue={currentRecord?.ips ? currentRecord.ips.join(', ') : ''} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Remark</Form.Label>
                <Form.Control className="form-feild" as="textarea" rows={2} name="remark" defaultValue={currentRecord?.remark || ''} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Remarks (Additional)</Form.Label>
                <Form.Control className="form-feild" as="textarea" rows={2} name="remarks" defaultValue={currentRecord?.remarks || ''} />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end mt-4 mb-4">
            <Button variant="secondary" onClick={handleCloseModal} className="me-2">Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );

  const extraModals = (
    <>
      {showDetailsModal && currentRecord && (
        <LicenseInfoDetails
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          licenseId={currentRecord.id}
          onEditLicense={(license) => { setShowDetailsModal(false); handleShowModal(license); }}
        />
      )}

      {selectedTenantData && (
        <TenantInfoDetails
          show={showTenantDetails}
          onHide={() => {
            setShowTenantDetails(false);
            setSelectedTenantData(null);
          }}
          tenantData={selectedTenantData}
          setupId={selectedTenantData.setup_id}
          onLicensesUpdated={fetchLicenseInfos}
        />
      )}

      <Offcanvas show={showExtendModal} onHide={() => setShowExtendModal(false)} placement="end" style={{ width: '500px', zIndex: 1060 }}>
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>Extend License Date</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form onSubmit={handleExtendSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Extension Value</Form.Label>
                  <Form.Control 
                    type="number" 
                    min="1"
                    required
                    value={extendData.value}
                    onChange={e => setExtendData({...extendData, value: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Extension Type</Form.Label>
                  <Form.Select 
                    value={extendData.type}
                    onChange={e => setExtendData({...extendData, type: e.target.value})}
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-4">
              <Button variant="secondary" onClick={() => setShowExtendModal(false)} className="me-2">Cancel</Button>
              <Button variant="success" type="submit">Extend License</Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );

  if (isEmbedded) {
    return (
      <>
        {innerContent}
        {licenseModal}
        {extraModals}
      </>
    );
  }

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row w-100">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">V360 License Info</h4>
              </div>
            </div>
          </div>
          {innerContent}
          {licenseModal}
          {extraModals}
        </div>
      </div>
    </div>
  );
};

export default LicenseInfoList;
